import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { setAddMsgInCalendarContact } from "@common-reducers/WhatsAppReducer";
import { addScheduledMessageButtonActions, selectScheduleRecipients } from "@common-reducers/AddScheduledMessageButtonReducer";
import { ContactSelect } from "./contact-select";
import { StatusRecipientToggle } from "./status-recipient-toggle";
import { hasStatusRecipient } from "@common/scheduling/status-recipient";
// Single source for the contact-picker shape — was duplicated as
// `MessageContact` here and inside contact-select.tsx (#40).
import type { ContactPickItem as MessageContact } from "../schedule-message-types";
export type { ContactPickItem as MessageContact } from "../schedule-message-types";

// FE#230 — the recipient selection lives entirely in Redux
// (AddScheduledMessageButtonReducer.recipients), derived here through the
// memoized `selectScheduleRecipients` selector. There is deliberately NO local
// `useState` copy and NO `useEffect` sync: the previous effect mirrored
// `message.contactList` into local state and re-filtered it on every change, so a
// mid-session `state.message` re-init (which reset contactList to a single
// empty-id placeholder) wiped the accumulated groups and only the first survived
// to persist. Now add/remove dispatch straight to Redux and the selector is the
// single source of truth — a body edit or a re-init can no longer touch it.
/** `gap` — space between the destination toggle and the contact field; the
 *  compose card passes its own spacing unit so the header reads evenly. */
export const SendMessageTo = ({ gap = 2 }: { gap?: number | string } = {}) => {
  const dispatch = useAppDispatch();
  const selectedContacts = useAppSelector(selectScheduleRecipients) as MessageContact[];

  // Add a recipient — dispatch straight to Redux. The reducer dedupes and ignores
  // empty ids, so the picker doesn't need its own guard.
  const handleAddContact = (contact: MessageContact) => {
    dispatch(addScheduledMessageButtonActions.addRecipient(contact));
    // Keep the "active" contact (used by the compose preview) in sync with the
    // latest pick — event-driven, no effect needed.
    dispatch(setAddMsgInCalendarContact(contact));
  };

  // Remove a recipient — dispatch straight to Redux, then point the active
  // contact at whatever remains (or clear it).
  const handleRemoveContact = (contactId: string) => {
    dispatch(addScheduledMessageButtonActions.removeRecipient(contactId));
    const remaining = selectedContacts.filter((c) => c.id !== contactId);
    dispatch(setAddMsgInCalendarContact(remaining.length > 0 ? remaining[remaining.length - 1] : null));
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap,
      width: '100%',
    }}>
      {/* FE#335 — "Schedule a Status" toggle. When Status is the target it is
          mutually exclusive with chats/groups, so the contact picker is hidden. */}
      <StatusRecipientToggle />
      {!hasStatusRecipient(selectedContacts) && (
        <ContactSelect
          onContactSelect={handleAddContact}
          onContactRemove={handleRemoveContact}
          selectedContactIds={selectedContacts.map(c => c.id)}
          selectedContacts={selectedContacts}
        />
      )}
    </Box>
  );
}
